import { Button, Flex, Spacing, Switch, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  AutomaticPaymentRemindersType,
  useTrackAutomaticPaymentReminders,
} from '@features/invoicingSettings/modules/paymentsAndReminders'

import { useFormContext } from '../../../../../../../../../../../../hooks'
import { InvoiceSettingsForm } from '../../../../../../types/invoiceSettingsForm'
import { InvoiceSettingsFormSchema } from '../../../../../../types/invoiceSettingsFormSchema'
import { UpsellOverlay } from '../../styles'
import * as Styled from './styles'

const paymentRemindersFieldPathByType: Record<AutomaticPaymentRemindersType, Path<InvoiceSettingsForm>> = {
  [AutomaticPaymentRemindersType.AfterDueDate]: 'automaticPaymentRemindersSettings.afterDueDate.active',
  [AutomaticPaymentRemindersType.BeforeDueDate]: 'automaticPaymentRemindersSettings.beforeDueDate.active',
  [AutomaticPaymentRemindersType.OnDueDate]: 'automaticPaymentRemindersSettings.onDueDate.active',
}

interface AutomaticPaymentRemindersRowProps {
  isLockedByUpsell: boolean
  onEdit: (paymentRemindersType: AutomaticPaymentRemindersType) => void
  openSubscriptionModal: () => void
  paymentRemindersType: AutomaticPaymentRemindersType
}

export const AutomaticPaymentRemindersRow = ({
  isLockedByUpsell,
  onEdit,
  openSubscriptionModal,
  paymentRemindersType,
}: AutomaticPaymentRemindersRowProps): ReactElement => {
  const { t } = useTranslation()
  const { FormItem, watch } = useFormContext<InvoiceSettingsFormSchema>()
  const { trackAutomaticPaymentReminders } = useTrackAutomaticPaymentReminders()

  const afterDueDateDays = watch('automaticPaymentRemindersSettings.afterDueDate.nDays')
  const beforeDueDateDays = watch('automaticPaymentRemindersSettings.beforeDueDate.nDays')

  const { description, title } = useMemo(() => {
    switch (paymentRemindersType) {
      case AutomaticPaymentRemindersType.AfterDueDate:
        return {
          title: t('settings.organization.external_invoice.automatic_payment_reminders.after_due_date.title'),
          description: t(
            'settings.organization.external_invoice.automatic_payment_reminders.after_due_date.description',
            { days: afterDueDateDays },
          ),
        }
      case AutomaticPaymentRemindersType.BeforeDueDate:
        return {
          title: t('settings.organization.external_invoice.automatic_payment_reminders.before_due_date.title'),
          description: t(
            'settings.organization.external_invoice.automatic_payment_reminders.before_due_date.description',
            { days: beforeDueDateDays },
          ),
        }
      case AutomaticPaymentRemindersType.OnDueDate:
        return {
          title: t('settings.organization.external_invoice.automatic_payment_reminders.on_due_date.title'),
        }
    }
  }, [afterDueDateDays, beforeDueDateDays, paymentRemindersType, t])

  const handleEditButtonClick = useCallback(() => {
    onEdit(paymentRemindersType)
    trackAutomaticPaymentReminders('modal opened', paymentRemindersType)
  }, [onEdit, paymentRemindersType, trackAutomaticPaymentReminders])

  return (
    <Flex alignItems="center" justifyContent="space-between">
      <Flex flexDirection="column">
        <Text weight="bold">{title}</Text>
        {description && <Text colorVariant="secondary">{description}</Text>}
      </Flex>
      <Flex alignItems="center" gap={Spacing.S12}>
        <Button icon="squarePen" onClick={handleEditButtonClick} size="sm" variant="text">
          {t('settings.organization.external_invoice.automatic_payment_reminders.edit')}
        </Button>
        <FormItem
          name={paymentRemindersFieldPathByType[paymentRemindersType]}
          render={({ field: { onChange, value } }) => (
            <Styled.SwitchWrapper>
              <Switch
                checked={!!value}
                disabled={isLockedByUpsell}
                onChange={(event) => {
                  onChange(event)
                  if (!value) {
                    trackAutomaticPaymentReminders('toggled on', paymentRemindersType)
                  }
                }}
              />
              {isLockedByUpsell && <UpsellOverlay onClick={openSubscriptionModal} />}
            </Styled.SwitchWrapper>
          )}
        />
      </Flex>
    </Flex>
  )
}
